import type Vue from "vue";
import { getCurrentInstance } from "vue";

/**
 * Composition API 移行（1-C）期間中の暫定 composable。
 *
 * クラスコンポーネントの `this.$root.$emit/$on/$off` を 1 箇所に隔離し、
 * クラス → Composition API 変換後も同じ呼び出し方を保てるようにする。
 * `getCurrentInstance()` 経由の `$root` アクセスをこのファイルへ閉じ込めることで、
 * 各コンポーネントの setup() が `getCurrentInstance()` で汚れるのを防ぐ。
 *
 * 1-E（PR-4）でこの composable の中身を mitt / Pinia action へ差し替える想定。
 * その際に利用側コンポーネントを無変更で済ませるのが狙い。
 *
 * NOTE: あくまで移行用の橋渡し。新規コードでの利用は避けること。
 */
export function useLegacyRootEvent() {
  const instance = getCurrentInstance();
  const root = instance?.proxy?.$root as Vue | undefined;
  if (!root) {
    throw new Error(
      "useLegacyRootEvent() は setup() 内で呼び出す必要があります",
    );
  }

  return {
    /** `this.$root.$emit(event, ...args)` 相当 */
    emit(event: string, ...args: unknown[]): void {
      root.$emit(event, ...args);
    },
    /** `this.$root.$on(event, handler)` 相当 */
    on<T extends unknown[]>(
      event: string,
      handler: (...args: T) => void,
    ): void {
      root.$on(event, handler);
    },
    /**
     * `this.$root.$off(event, handler?)` 相当。
     * handler を省略すると当該イベントの全リスナーを解除する（旧コードの挙動を踏襲）。
     */
    off<T extends unknown[]>(
      event: string,
      handler?: (...args: T) => void,
    ): void {
      root.$off(event, handler);
    },
  };
}
